import { useMutation, useQuery } from "@apollo/client";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  CREATE_SERVICE_TEMPLATE,
  DELETE_SERVICE_TEMPLATE,
  GET_SERVICE_TEMPLATES,
} from "@/graphql/service-templates.graphql";
import { useGraphQLFormattedErrorHandler } from "@/lib/graphql/error-handler";
import { useSalonStore } from "@/lib/store/salon";

export function useServiceTemplates() {
  const { t } = useTranslation("settings");
  const franchise = useSalonStore((s) => s.getCurrentFranchise());
  const franchiseId = franchise?.franchiseId;
  const { processApolloError } = useGraphQLFormattedErrorHandler();

  // Query templates
  const { data, loading, error, refetch } = useQuery(GET_SERVICE_TEMPLATES, {
    variables: { franchiseId: franchiseId || "" },
    skip: !franchiseId,
    fetchPolicy: "cache-and-network",
  });

  // Create template mutation
  const [createTemplateMutation, { loading: creating }] = useMutation(CREATE_SERVICE_TEMPLATE, {
    onCompleted: () => {
      toast.success(t("serviceTemplates.messages.createSuccess"));
      refetch();
    },
    onError: (error) => {
      processApolloError(error);
    },
  });

  // Delete template mutation
  const [deleteTemplateMutation, { loading: deleting }] = useMutation(DELETE_SERVICE_TEMPLATE, {
    onCompleted: () => {
      toast.success(t("serviceTemplates.messages.deleteSuccess"));
      refetch();
    },
    onError: (error) => {
      processApolloError(error);
    },
  });

  const templates = data?.getServiceTemplates || [];
  const systemTemplates = templates.filter((t) => t.scope === "SYSTEM");
  const customTemplates = templates.filter((t) => t.scope === "FRANCHISE");

  const createTemplate = async (input: {
    name: Record<string, string>;
    storefrontDisplayMode: "TABLE" | "REGULAR";
    groups: {
      name: Record<string, string>;
      selectionType: string;
      minSelections: number;
      maxSelections?: number;
      options: {
        optionName: Record<string, string>;
        durationModifier: number;
        isDefault: boolean;
      }[];
    }[];
  }) => {
    if (!franchiseId) return;

    await createTemplateMutation({
      variables: {
        franchiseId,
        input,
      },
    });
  };

  const deleteTemplate = async (templateId: string) => {
    await deleteTemplateMutation({
      variables: { templateId },
    });
  };

  return {
    templates,
    systemTemplates,
    customTemplates,
    loading,
    error,
    creating,
    deleting,
    createTemplate,
    deleteTemplate,
    refetch,
  };
}
